import { decimalFormat, translate } from '../../utils';

export const INITIAL_VISIBLE_HOTEL_LIMIT = 10;

export const PERIOD_OPTIONS = [
    {
        value: 'daily',
        labelTranslationKey: 'PORTFOLIO_DASHBOARD_PERIOD_DAILY',
        comparisonPeriodTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_YESTERDAY',
        nextComparisonPeriodTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_TOMORROW',
        revenueLabelTranslationKey: 'PORTFOLIO_DASHBOARD_REVENUE_TODAY',
        extendedRevenueLabelTranslationKey: 'PORTFOLIO_DASHBOARD_REVENUE_THIS_WEEK',
        extendedComparisonTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_PREVIOUS_WEEK',
        extendedNextComparisonTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_NEXT_WEEK',
    },
];

const DEFAULT_PERIOD = PERIOD_OPTIONS[0].value;

/**
 * @param {import('./portfolioDashboard.types').PortfolioPeriod} selectedPeriod
 * @returns {import('./portfolioDashboard.types').PortfolioMetricDefinition[]}
 */
export function getPortfolioMetricDefinitions(selectedPeriod) {
    const periodOption = PERIOD_OPTIONS.find((periodOption) => periodOption.value === selectedPeriod);

    return [
        {
            metricKey: 'occupiedRooms',
            icon: 'fa fa-bed',
            labelTranslationKey: 'OCCP_ROOM',
            valueFormat: 'integer',
            changePercentageField: 'previousOccupiedRoomsChangePercentage',
            nextChangePercentageField: 'nextOccupiedRoomsChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'occupancy',
            icon: 'fa fa-shopping-bag',
            labelTranslationKey: 'Occupancy',
            valueFormat: 'percent',
            changePercentageField: 'previousOccupancyChangePercentage',
            nextChangePercentageField: 'nextOccupancyChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'adr',
            icon: 'fa fa-chart-column',
            labelTranslationKey: 'ADR',
            valueFormat: 'currency',
            changePercentageField: 'previousAdrChangePercentage',
            nextChangePercentageField: 'nextAdrChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'revPAR',
            icon: 'fa fa-user-plus',
            labelTranslationKey: 'REV_PAR',
            valueFormat: 'currency',
            changePercentageField: 'previousRevPARChangePercentage',
            nextChangePercentageField: 'nextRevPARChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'revenue',
            icon: 'fa fa-coin',
            labelTranslationKey: periodOption.revenueLabelTranslationKey,
            valueFormat: 'compactCurrency',
            changePercentageField: 'previousRevenueChangePercentage',
            nextChangePercentageField: 'nextRevenueChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'extendedRevenue',
            icon: 'fa fa-coins',
            labelTranslationKey: periodOption.extendedRevenueLabelTranslationKey,
            valueFormat: 'compactCurrency',
            changePercentageField: 'previousRevenueChangePercentage',
            nextChangePercentageField: 'nextRevenueChangePercentage',
            usesExtendedComparison: true,
        },
    ];
}

/**
 * @param {string[] | null | undefined} selectedPropertiesKeys
 * @param {'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'} selectedPeriod
 * @returns {URLSearchParams}
 */
export function buildSearchParams(selectedPropertiesKeys, selectedPeriod) {
    const searchParams = new URLSearchParams();

    if (selectedPeriod !== DEFAULT_PERIOD) {
        searchParams.set('selectedPeriod', selectedPeriod);
    }

    selectedPropertiesKeys?.forEach((propertyKey) => searchParams.append('selectedPropertiesKeys[]', propertyKey));

    return searchParams;
}

const PERCENTAGE_DIFF_APPEARANCE = {
    1: {
        colorClass: 'text-green',
        iconClass: 'fa fa-angle-up',
        signPrefix: '+',
    },
    [-1]: {
        colorClass: 'text-red',
        iconClass: 'fa fa-angle-down',
        signPrefix: '',
    },
    0: {
        colorClass: 'text-muted',
        iconClass: '',
        signPrefix: '',
    },
};

/**
 * @param {number} changePercent
 * @param {boolean} [higherIsBetter=true]
 * @returns {{ colorClass: string, iconClass: string, signPrefix: string }}
 */
export function getPercentageDiffAppearance(changePercent, higherIsBetter = true) {
    const changeSign = Math.sign(changePercent);
    const { iconClass, signPrefix } = PERCENTAGE_DIFF_APPEARANCE[changeSign];
    const { colorClass } = PERCENTAGE_DIFF_APPEARANCE[higherIsBetter ? changeSign : -changeSign];

    return { colorClass, iconClass, signPrefix };
}

const COMPACT_CURRENCY_MILLIONS_THRESHOLD = 500000;

/**
 * @param {'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'} selectedPeriod
 * @returns {string}
 */
export function getMetricComparisonTranslationKey(selectedPeriod, useExtendedComparison = false) {
    const periodOption = PERIOD_OPTIONS.find((periodOption) => periodOption.value === selectedPeriod);

    if (useExtendedComparison) return periodOption.extendedComparisonTranslationKey;

    return periodOption.comparisonPeriodTranslationKey;
}

/**
 * @param {'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'} selectedPeriod
 * @returns {string}
 */
export function getMetricNextComparisonTranslationKey(selectedPeriod, useExtendedComparison = false) {
    const periodOption = PERIOD_OPTIONS.find((periodOption) => periodOption.value === selectedPeriod);

    if (useExtendedComparison) return periodOption.extendedNextComparisonTranslationKey;

    return periodOption.nextComparisonPeriodTranslationKey;
}

/**
 * @param {{ value: number, valueFormat: 'integer' | 'percent' | 'currency' | 'compactCurrency' | string }} metric
 * @param {string} currency
 * @returns {string}
 */
export function formatMetricValue(metric, currency) {
    switch (metric.valueFormat) {
        case 'integer':
            return decimalFormat(metric.value, 0);
        case 'percent':
            return `${decimalFormat(metric.value * 100, 1)} %`;
        case 'currency':
            return `${decimalFormat(metric.value, 2)} ${currency}`;
        case 'compactCurrency':
            return formatCompactCurrency(metric.value, currency);
        default:
            return String(metric.value);
    }
}

/**
 * @param {number} value
 * @param {string} currency
 * @returns {string}
 */
function formatCompactCurrency(value, currency) {
    if (value >= COMPACT_CURRENCY_MILLIONS_THRESHOLD) {
        return translate('frontend', 'PORTFOLIO_DASHBOARD_COMPACT_CURRENCY_MILLIONS', {
            value: decimalFormat(value / 1000000, 2),
            currency,
        });
    }

    return translate('frontend', 'PORTFOLIO_DASHBOARD_COMPACT_CURRENCY_THOUSANDS', {
        value: decimalFormat(value / 1000, 2),
        currency,
    });
}
